import React from "react";
import {
  awards,
  patents,
  type Award,
  type Patent,
} from "@/data/patentsAwardsData";

const PatentsAwards: React.FC = () => {
  const formatYear = (year: number) => year.toString();

  const AwardCard: React.FC<{ award: Award }> = ({ award }) => (
    <div className="border border-black p-4 mb-4">
      <div className="flex justify-between items-start mb-2">
        <h3 className="font-mono text-sm font-bold">{award.title}</h3>
        <span className="font-mono text-xs text-gray-500">
          {formatYear(award.year)}
        </span>
      </div>

      <div className="font-mono text-xs mb-2">
        <span className="font-bold">Organization:</span> {award.organization}
      </div>

      {award.category && (
        <div className="font-mono text-xs mb-2">
          <span className="font-bold">Category:</span> {award.category}
        </div>
      )}

      <p className="font-mono text-xs text-gray-700 mb-3">
        {award.description}
      </p>

      <div className="flex items-center gap-4">
        {award.link && (
          <a
            href={award.link}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-xs link-minimal"
          >
            View Award →
          </a>
        )}

        {award.image && (
          <div className="text-xs font-mono text-gray-500">
            📸 Award Image Available
          </div>
        )}
      </div>
    </div>
  );

  const PatentCard: React.FC<{ patent: Patent }> = ({ patent }) => (
    <div className="border border-black p-4 mb-4">
      <div className="flex justify-between items-start mb-2">
        <h3 className="font-mono text-sm font-bold">{patent.title}</h3>
        <span className="font-mono text-xs text-gray-500">
          {formatYear(patent.year)}
        </span>
      </div>

      <div className="font-mono text-xs mb-2">
        <span className="font-bold">Patent Number:</span> {patent.patentNumber}
      </div>

      <div className="font-mono text-xs mb-2">
        <span className="font-bold">Status:</span>
        <span
          className={`ml-1 px-2 py-1 border border-black text-xs ${
            patent.status === "Granted"
              ? "bg-black text-white"
              : patent.status === "Pending"
                ? "bg-white text-black"
                : "bg-gray-100 text-gray-600"
          }`}
        >
          {patent.status}
        </span>
      </div>

      <div className="font-mono text-xs mb-2">
        <span className="font-bold">Inventors:</span>{" "}
        {patent.inventors.join(", ")}
      </div>

      <div className="font-mono text-xs mb-2">
        <span className="font-bold">Assignee:</span> {patent.assignee}
      </div>

      <p className="font-mono text-xs text-gray-700 mb-3">
        {patent.description}
      </p>

      {patent.link && (
        <a
          href={patent.link}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono text-xs link-minimal"
        >
          View Patent →
        </a>
      )}
    </div>
  );

  return (
    <div className="max-w-2xl">
      <h1 className="text-3xl font-bold mb-8 font-mono">Patents & Awards</h1>

      {/* Patents Section */}
      <section>
        <h2 className="text-xl font-bold mb-6 font-mono border-l-4 border-black pl-4">
          Patents
        </h2>

        {patents.length > 0 ? (
          <div>
            {patents.map((patent) => (
              <PatentCard key={patent.id} patent={patent} />
            ))}
          </div>
        ) : (
          <div className="font-mono text-sm text-gray-500 border border-black p-4">
            No patents data available.
          </div>
        )}
      </section>
      {/* Awards Section */}
      <section className="mb-12">
        <h2 className="text-xl font-bold mb-6 font-mono border-l-4 border-black pl-4">
          Awards & Recognition
        </h2>

        {awards.length > 0 ? (
          <div>
            {awards.map((award) => (
              <AwardCard key={award.id} award={award} />
            ))}
          </div>
        ) : (
          <div className="font-mono text-sm text-gray-500 border border-black p-4">
            No awards data available.
          </div>
        )}
      </section>
    </div>
  );
};

export default PatentsAwards;
